import { useState, useCallback } from 'react';
import { useFullGraph, useGraphStats } from '../api/graph';
import { useAppStore } from '../store';
import ForceGraph from '../components/graph/ForceGraph';
import Graph3D from '../components/graph/Graph3D';
import type { GraphNode, EntityType } from '../types';

const ENTITY_TYPES: EntityType[] = [
  'person', 'organization', 'location', 'ip_address', 'domain',
  'email', 'phone_number', 'financial_account', 'vehicle', 'document',
];

export default function GraphExplorer() {
  const graphViewMode = useAppStore((s) => s.graphViewMode);
  const setGraphViewMode = useAppStore((s) => s.setGraphViewMode);

  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
  const [nodeLimit, setNodeLimit] = useState(500);
  const [filterTypes, setFilterTypes] = useState<Set<EntityType>>(new Set(ENTITY_TYPES));
  const [minConfidence, setMinConfidence] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const { data: graphData, isLoading } = useFullGraph(nodeLimit);
  const { data: stats } = useGraphStats();

  // Filter graph data
  const filteredNodes = graphData?.nodes.filter(
    (n) => filterTypes.has(n.type) && (n.risk_score >= 0 || true)
  ) ?? [];
  const filteredNodeIds = new Set(filteredNodes.map((n) => n.id));
  const filteredEdges = graphData?.edges.filter(
    (e) =>
      filteredNodeIds.has(e.source) &&
      filteredNodeIds.has(e.target) &&
      e.confidence >= minConfidence
  ) ?? [];

  const handleNodeClick = useCallback((node: GraphNode) => {
    setSelectedNode(node);
  }, []);

  const handleNodeHover = useCallback((node: GraphNode | null) => {
    setHoveredNode(node);
  }, []);

  function toggleType(type: EntityType) {
    const next = new Set(filterTypes);
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }
    setFilterTypes(next);
  }

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] animate-fade-in">
      {/* Top controls */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Graph Explorer</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {filteredNodes.length} nodes, {filteredEdges.length} edges
            {stats && ` | ${stats.communities} communities`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-surface-tertiary rounded-lg p-0.5">
            <button
              onClick={() => setGraphViewMode('2d')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                graphViewMode === '2d'
                  ? 'bg-helios-700 text-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              2D
            </button>
            <button
              onClick={() => setGraphViewMode('3d')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                graphViewMode === '3d'
                  ? 'bg-helios-700 text-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              3D
            </button>
          </div>

          {/* Filters toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showFilters
                ? 'bg-helios-700 text-cyber-cyan'
                : 'bg-surface-tertiary text-gray-400 hover:text-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
            </svg>
          </button>

          {/* Node limit */}
          <select
            value={nodeLimit}
            onChange={(e) => setNodeLimit(Number(e.target.value))}
            className="px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-xs text-gray-300 focus:outline-none"
          >
            <option value={100}>100 nodes</option>
            <option value={250}>250 nodes</option>
            <option value={500}>500 nodes</option>
            <option value={1000}>1000 nodes</option>
          </select>

          {/* Export */}
          <button className="px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-xs text-gray-400 hover:text-gray-200 transition-colors">
            Export
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 gap-4 min-h-0">
        {/* Filter panel */}
        {showFilters && (
          <div className="w-56 flex-shrink-0 glass-panel p-4 space-y-4 overflow-y-auto">
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">Entity Types</h3>
              <div className="space-y-1.5">
                {ENTITY_TYPES.map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={filterTypes.has(type)}
                      onChange={() => toggleType(type)}
                      className="rounded border-border-subtle bg-surface-tertiary text-cyber-cyan focus:ring-cyber-cyan/30"
                    />
                    <span className="capitalize text-xs">{type.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                Min Confidence: {Math.round(minConfidence * 100)}%
              </h3>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={minConfidence}
                onChange={(e) => setMinConfidence(Number(e.target.value))}
                className="w-full accent-cyber-cyan"
              />
            </div>

            {stats && (
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">Graph Stats</h3>
                <div className="space-y-1 text-xs text-gray-400">
                  <div className="flex justify-between">
                    <span>Total nodes</span>
                    <span className="text-gray-300 font-mono">{stats.total_nodes}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total edges</span>
                    <span className="text-gray-300 font-mono">{stats.total_edges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg degree</span>
                    <span className="text-gray-300 font-mono">{stats.avg_degree.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Density</span>
                    <span className="text-gray-300 font-mono">{(stats.density * 100).toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Communities</span>
                    <span className="text-gray-300 font-mono">{stats.communities}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Graph viewport */}
        <div className="flex-1 glass-panel overflow-hidden relative">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                <p className="text-sm text-gray-500">Loading graph data...</p>
              </div>
            </div>
          ) : filteredNodes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-500">No graph data available</p>
            </div>
          ) : graphViewMode === '2d' ? (
            <ForceGraph
              nodes={filteredNodes}
              edges={filteredEdges}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              selectedNodeId={selectedNode?.id}
            />
          ) : (
            <Graph3D
              nodes={filteredNodes}
              edges={filteredEdges}
              onNodeClick={handleNodeClick}
              selectedNodeId={selectedNode?.id}
            />
          )}

          {/* Hovered node info */}
          {hoveredNode && (
            <div className="absolute top-4 left-4 bg-surface-elevated/90 border border-border-subtle rounded-lg px-3 py-2 text-xs backdrop-blur-sm">
              <p className="font-semibold text-gray-200">{hoveredNode.label}</p>
              <p className="text-gray-400 capitalize">{hoveredNode.type.replace('_', ' ')}</p>
            </div>
          )}
        </div>

        {/* Node details panel */}
        {selectedNode && (
          <div className="w-72 flex-shrink-0 glass-panel p-4 space-y-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-300">Node Details</h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="p-1 rounded hover:bg-surface-tertiary text-gray-500 hover:text-gray-300"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-100">{selectedNode.label}</h2>
              <p className="text-xs text-gray-500 capitalize mt-1">{selectedNode.type.replace('_', ' ')}</p>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Risk Score</span>
                <span className="text-gray-300 font-mono">{Math.round(selectedNode.risk_score * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Connections</span>
                <span className="text-gray-300 font-mono">
                  {filteredEdges.filter(
                    (e) => e.source === selectedNode.id || e.target === selectedNode.id
                  ).length}
                </span>
              </div>
            </div>

            {Object.keys(selectedNode.properties).length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Properties</p>
                <div className="space-y-1.5">
                  {Object.entries(selectedNode.properties).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span className="text-gray-500 capitalize">{k.replace('_', ' ')}</span>
                      <span className="text-gray-300 text-right max-w-[60%] truncate">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-border-subtle">
              <button className="flex-1 px-3 py-2 text-xs font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">
                Expand
              </button>
              <button className="flex-1 px-3 py-2 text-xs font-medium bg-surface-tertiary text-gray-400 rounded-lg hover:text-gray-200 transition-colors">
                Details
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
